import React, { useState } from "react";

import { faArrowDownToLine, faEllipsis, faSparkles } from "@performyard/fontawesome/pro-solid-svg-icons";
import { DropDownMenu, type DropDownMenuOption, Icon } from "@performyard/py-components";
import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";

import DownloadButtonSSE from "../../../../components.smart/DownloadButtonSSE";
import { MeetingWorkspaceInsightModule } from "../../../../components/AIModules/MeetingWorkspaceInsight";

import { useAiSettings } from "../../../../api/hooks";
import { useUserAsyncTask } from "../../../../hooks/useUserAsyncTask";

import { exportWorkspaceMeetingsCsv } from "../../../../api/services/defs/internal-api/meetings";

interface WorkspaceActionsDropdownProps {
	workspaceId: string;
	filters: Record<string, any>;
	sort: { name: string; direction: "asc" | "desc" };
	isAdmin?: boolean;
	meetings?: Array<{ status: string }>;
}

const WorkspaceActionsDropdown = ({
	workspaceId,
	filters,
	isAdmin = false,
	meetings = [],
}: WorkspaceActionsDropdownProps) => {
	const [isAIInsightsOpen, setIsAIInsightsOpen] = useState(false);

	const { data: aiSettings } = useAiSettings();

	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(
		ASYNC_TASK_NAMES.EXPORT_WORKSPACE_MEETINGS_CSV
	);

	const isAIInsightsEnabled = aiSettings?.meetingWorkspaceInsightsEnabled;

	// Check if all meetings are cancelled or no meetings exist
	const hasNoValidMeetings = meetings.length === 0;
	const allMeetingsCancelled = meetings.length > 0 && meetings.every((meeting) => meeting.status === "canceled");
	const isAIInsightsDisabled = hasNoValidMeetings;

	const handleDownloadCsv = async () => {
		await triggerExport(() =>
			exportWorkspaceMeetingsCsv(workspaceId, {
				filters,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			})
		);
	};

	const handleAIInsights = () => {
		setIsAIInsightsOpen(true);
	};

	const handleDropDownSelect = (key: string) => {
		switch (key) {
			case "DownloadCSV": {
				handleDownloadCsv();
				break;
			}
			case "AIInsights": {
				handleAIInsights();
				break;
			}
			default:
				break;
		}
	};

	const actionsDropDown: DropDownMenuOption[] = [
		...(isAIInsightsEnabled
			? [
					{
						key: "AIInsights",
						label: "AI Insights",
						right: <Icon icon={faSparkles} size={16} color="neutral-5" />,
						disabled: isAIInsightsDisabled,
					},
				]
			: []),
		{
			key: "DownloadCSV",
			label: "Download CSV",
			right: <Icon icon={faArrowDownToLine} size={16} color="neutral-5" />,
		},
	];

	return (
		<>
			<DropDownMenu
				options={actionsDropDown}
				onSelect={handleDropDownSelect}
				minWidth={170}
				placement="bottom-end"
				bordered
			>
				<Icon icon={faEllipsis} size={16} />
			</DropDownMenu>
			<div style={{ display: "none" }}>
				<DownloadButtonSSE task={exportTask} onClick={handleDownloadCsv}>
					Download CSV
				</DownloadButtonSSE>
			</div>
			{isAIInsightsEnabled && (
				<MeetingWorkspaceInsightModule
					workspaceId={workspaceId}
					isOpen={isAIInsightsOpen}
					onOpenChange={setIsAIInsightsOpen}
					filters={filters}
					isAdmin={isAdmin}
					allMeetingsCancelled={allMeetingsCancelled}
				/>
			)}
		</>
	);
};

export default WorkspaceActionsDropdown;
